/**
 * Left-column tabbed content — the 8 tabs from the KC2 reference:
 *   Timeline · Checklist · Additional Properties · Client Info ·
 *   Global Notes · Transfers · RCA · Activity
 *
 * The reply composer lives in its own drawer (opened from the header), so the
 * Timeline is read-only here. Global Notes has an "Add note" action that opens
 * that drawer in note mode.
 */
import { useMemo, type JSX } from 'react'
import { cn } from '../../../utils/cn.js'
import type {
  ActivityLogEntry,
  ConversationDetailResponse,
  Message,
} from '../../../modules/conversations/types.js'
import ConversationTimeline from './ConversationTimeline.js'
import ActivityTimeline from './ActivityTimeline.js'
import AdditionalPropertiesSection from './AdditionalPropertiesSection.js'
import ClientInfoTab from './ClientInfoTab.js'
import TransferHistoryTab from './TransferHistoryTab.js'
import ChecklistTab from './ChecklistTab.js'
import RcaTab from './RcaTab.js'
import GlobalNotesTab from './GlobalNotesTab.js'
import ApprovalsTab from './ApprovalsTab.js'
import ParentChildTab from './ParentChildTab.js'
import LinkedTab from './LinkedTab.js'
import MergedTab from './MergedTab.js'
import CollaboratorsTab from './CollaboratorsTab.js'
import type { MergeCandidate } from './MergeDrawer.js'
import { useConversationTransfers } from './useConversationTransfers.js'
import { R } from './primitives.js'

type Conversation = ConversationDetailResponse['conversation']
export type TabKey =
  | 'timeline'
  | 'checklist'
  | 'additional'
  | 'client'
  | 'notes'
  | 'transfers'
  | 'approvals'
  | 'rca'
  | 'activity'
  | 'linked'
  | 'merged'
  | 'collaborators'
  | 'parentchild'

const TABS: { key: TabKey; label: string }[] = [
  { key: 'timeline', label: 'Timeline' },
  { key: 'checklist', label: 'Checklist' },
  { key: 'additional', label: 'Additional Properties' },
  { key: 'client', label: 'Client Info' },
  { key: 'notes', label: 'Global Notes' },
  { key: 'transfers', label: 'Transfers' },
  { key: 'approvals', label: 'Approvals' },
  { key: 'rca', label: 'RCA' },
  { key: 'activity', label: 'Activity' },
  { key: 'linked', label: 'Linked' },
  { key: 'merged', label: 'Merged' },
  { key: 'collaborators', label: 'Collaborators' },
  { key: 'parentchild', label: 'Parent/Child' },
]

export interface ConversationTabsProps {
  conversation: Conversation
  messages: Message[]
  activityLog: ActivityLogEntry[]
  currentAgentId: string | null
  ratingScale: number
  canEdit: boolean
  tab: TabKey
  onTabChange: (t: TabKey) => void
  onAddNote: () => void
  /** Open the Add-Child flow (from the Parent/Child tab's Create button). */
  onCreateChild?: (() => void) | undefined
  /** Whether the existing Add Child flow is allowed for this conversation. */
  canAddChild?: boolean | undefined
  addChildDisabledReason?: string | undefined
  /** Open the Link flow (from the Linked tab's Link button). */
  onLinkConversation?: (() => void) | undefined
  /** Bumped after a link is added, to force the Linked tab to refetch. */
  linkedRefreshKey?: number | undefined
  /** Bumped after a transfer event, to force the Transfers tab to refetch. */
  transfersRefreshKey?: number | undefined
  /** Open a related conversation in-place (parent/child/linked navigation). */
  onNavigateToConversation?: ((conversationId: string) => void) | undefined
  /** Merge selected conversations from the Client Info tab. */
  onMergeTickets?: ((candidates: MergeCandidate[]) => void) | undefined
  /** Remove a collaborator (Collaborators tab). */
  onRemoveCoAgent?: ((agentId: string) => Promise<boolean>) | undefined
  /** Whether a conversation action is in flight (disables the remove button). */
  busy?: boolean | undefined
  onChanged: () => void
  onAttachmentSelect?: ((attachmentId: string) => void) | undefined
  /** Whether the viewer may send on this conversation — gates the Timeline's
      inline Reply / Forward composer. */
  canReply?: boolean | undefined
  appendMessage?: ((m: Message) => void) | undefined
  replaceMessage?: ((oldId: string, m: Message) => void) | undefined
  markMessageFailed?: ((id: string, error: string) => void) | undefined
}

function TabButton({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean
  onClick: () => void
  label: string
  count?: number | undefined
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative -mb-px whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-semibold transition-colors',
        active ? 'border-[#3366FF] text-[#3366FF]' : 'border-transparent text-slate-500 hover:text-slate-700',
      )}
    >
      {label}
      {count !== undefined && count > 0 && (
        <span
          className={`ml-1.5 ${R} ${active ? 'bg-blue-50 text-[#3366FF]' : 'bg-slate-100 text-slate-500'} px-1.5 py-0.5 text-[10px] font-bold`}
        >
          {count}
        </span>
      )}
    </button>
  )
}

export default function ConversationTabs(props: ConversationTabsProps): JSX.Element {
  const { conversation, messages, activityLog, tab, onTabChange: setTab, onAttachmentSelect } = props

  const transferActivity = useMemo(
    () => activityLog.filter((e) => /transfer/i.test(e.eventType)),
    [activityLog],
  )
  const { transfers, loading: transfersLoading } = useConversationTransfers(
    conversation.id,
    props.transfersRefreshKey,
  )

  // Relationship tabs are shown only when they have content.
  const showParentChildTab = conversation.parentId !== null || conversation.isParent === true
  const showLinkedTab = (conversation.linkedCount ?? 0) > 0
  // Merged tab: this conversation is a secondary (points at a primary) OR a primary
  // (has conversations merged into it).
  const showMergedTab = conversation.mergedIntoId !== null || (conversation.mergedSecondaryCount ?? 0) > 0
  const showCollaboratorsTab = (conversation.coAgentIds?.length ?? 0) > 0

  const visibleTabs = useMemo(() => {
    return TABS.filter((t) => {
      if (t.key === 'parentchild') return showParentChildTab
      if (t.key === 'linked') return showLinkedTab
      if (t.key === 'merged') return showMergedTab
      if (t.key === 'collaborators') return showCollaboratorsTab
      return true
    })
  }, [showParentChildTab, showLinkedTab, showMergedTab, showCollaboratorsTab])

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Tab bar (scrolls horizontally if cramped) */}
      <div className="scrollbar-hide flex shrink-0 items-center gap-1 overflow-x-auto border-b border-slate-200 px-4">
        {visibleTabs.map((t) => (
          <TabButton
            key={t.key}
            active={tab === t.key}
            onClick={() => setTab(t.key)}
            label={t.label}
            count={t.key === 'transfers' ? transferActivity.length : undefined}
          />
        ))}
      </div>

      {/* Timeline: Gmail-style thread with inline Reply / Forward */}
      {tab === 'timeline' && (
        <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/50">
          <ConversationTimeline
            conversation={conversation}
            messages={messages}
            activityLog={activityLog}
            currentAgentId={props.currentAgentId}
            onAttachmentSelect={onAttachmentSelect}
            canReply={props.canReply}
            appendMessage={props.appendMessage}
            replaceMessage={props.replaceMessage}
            markMessageFailed={props.markMessageFailed}
            ratingScale={props.ratingScale}
          />
        </div>
      )}

      {tab === 'checklist' && (
        <ChecklistTab
          conversationId={conversation.id}
          canEdit={props.canEdit}
          onChanged={props.onChanged}
        />
      )}

      {tab === 'additional' && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <AdditionalPropertiesSection
            conversationId={conversation.id}
            conversationFormId={conversation.conversationFormId}
            values={conversation.additionalProperties}
            canEdit={props.canEdit}
            onChanged={props.onChanged}
          />
        </div>
      )}

      {tab === 'client' && (
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          <ClientInfoTab
            conversation={conversation}
            onNavigateToConversation={props.onNavigateToConversation}
            onMergeTickets={props.onMergeTickets}
          />
        </div>
      )}

      {tab === 'notes' && (
        <GlobalNotesTab
          conversationId={conversation.id}
          canEdit={props.canEdit}
          onAttachmentSelect={onAttachmentSelect}
        />
      )}

      {tab === 'transfers' && (
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          <TransferHistoryTab transfers={transfers} loading={transfersLoading} />
        </div>
      )}

      {tab === 'approvals' && (
        <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/50">
          <ApprovalsTab conversationId={conversation.id} />
        </div>
      )}

      {tab === 'rca' && (
        <RcaTab
          conversationId={conversation.id}
          categoryId={conversation.categoryId}
          subcategoryId={conversation.subcategoryId}
          canEdit={props.canEdit}
          onChanged={props.onChanged}
        />
      )}

      {tab === 'activity' && (
        <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 p-6">
          <ActivityTimeline entries={activityLog} />
        </div>
      )}

      {tab === 'linked' && (
        <LinkedTab
          conversationId={conversation.id}
          refreshKey={props.linkedRefreshKey}
          onNavigateToConversation={props.onNavigateToConversation ?? (() => undefined)}
          onLink={props.onLinkConversation ?? (() => undefined)}
        />
      )}

      {tab === 'merged' && (
        <MergedTab
          conversationId={conversation.id}
          mergedIntoId={(conversation.mergedIntoId as string | null) ?? null}
          onNavigateToConversation={props.onNavigateToConversation ?? (() => undefined)}
        />
      )}

      {tab === 'collaborators' && (
        <CollaboratorsTab
          coAgentIds={conversation.coAgentIds ?? []}
          canEdit={props.canEdit}
          onRemove={props.onRemoveCoAgent ?? (async () => false)}
          busy={props.busy ?? false}
        />
      )}

      {tab === 'parentchild' && (
        <ParentChildTab
          conversationId={conversation.id}
          parentId={(conversation.parentId as string | null) ?? null}
          isParent={(conversation.isParent as boolean) ?? false}
          onNavigateToConversation={props.onNavigateToConversation ?? (() => undefined)}
          onCreateChild={props.onCreateChild ?? (() => undefined)}
          canCreateChild={props.canAddChild}
          createChildDisabledReason={props.addChildDisabledReason}
        />
      )}
    </div>
  )
}
